/**
 * Scorecard — ported from the "OnFlow Design System" project
 * (v2/components/track/Scorecard.jsx).
 *
 * Week at a glance: one column per day, one row per commitment (diet,
 * training, weight). Answers "which days did I actually do the work" without
 * a single number on screen.
 */
import type { CSSProperties } from 'react';

export type ScoreMark = 'hit' | 'over' | 'under' | 'miss' | 'rest';

const MARK: Record<ScoreMark, { bg: string; fg: string; bd: string; g: string }> = {
  hit: { bg: 'var(--on-track-wash)', fg: 'var(--on-track)', bd: 'var(--on-track)', g: '✓' },
  over: { bg: 'var(--off-track-wash)', fg: 'var(--off-track)', bd: 'var(--off-track)', g: '↑' },
  under: { bg: 'var(--drifting-wash)', fg: 'var(--drifting)', bd: 'var(--drifting)', g: '↓' },
  miss: { bg: 'transparent', fg: 'var(--ink-3)', bd: 'var(--edge)', g: '·' },
  rest: { bg: 'var(--paper-2)', fg: 'var(--ink-3)', bd: 'var(--edge)', g: '–' },
};

export interface ScorecardDay {
  label: string;
  today?: boolean;
}

export interface ScorecardRow {
  label: string;
  marks: ReadonlyArray<ScoreMark>;
}

interface ScorecardProps {
  days: ReadonlyArray<ScorecardDay>;
  rows: ReadonlyArray<ScorecardRow>;
  style?: CSSProperties;
}

export function Scorecard({ days, rows, style }: ScorecardProps) {
  return (
    <div style={style}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `58px repeat(${days.length}, 1fr)`,
          gap: 6,
          alignItems: 'center',
        }}
      >
        <span />
        {days.map((d, i) => (
          <span
            key={i}
            style={{
              textAlign: 'center',
              fontFamily: 'var(--font-mono)',
              fontSize: 9,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: d.today ? 'var(--ink-1)' : 'var(--ink-3)',
              fontWeight: d.today ? 600 : 400,
            }}
          >
            {d.label}
          </span>
        ))}
        {rows.map((row) => (
          <Row key={row.label} row={row} />
        ))}
      </div>
    </div>
  );
}

function Row({ row }: { row: ScorecardRow }) {
  return (
    <>
      <span
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 10,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'var(--ink-3)',
        }}
      >
        {row.label}
      </span>
      {row.marks.map((m, i) => {
        const k = MARK[m] ?? MARK.miss;
        return (
          <span
            key={i}
            title={`${row.label}: ${m}`}
            style={{
              aspectRatio: '1',
              display: 'grid',
              placeItems: 'center',
              borderRadius: 'var(--r-xs)',
              background: k.bg,
              border: `1px solid color-mix(in srgb, ${k.bd} 45%, transparent)`,
              color: k.fg,
              fontFamily: 'var(--font-ui)',
              fontSize: 12,
              fontWeight: 700,
              lineHeight: 1,
            }}
          >
            {k.g}
          </span>
        );
      })}
    </>
  );
}
